import { useCallback, useState } from 'react'

import { useQuery } from '@apollo/client'
import { uniqBy } from 'lodash'

import { GET_LOGGED_IN_USER_PROJECTS } from '@/modules/user/graphQL'

import {
  LoggedInUserProjectsUiQuery,
  LoggedInUserProjectsUiQueryVariables,
} from 'types/api.graphql'

type Projects = LoggedInUserProjectsUiQuery['projectsForBuilder']

export const useProjectFetch = (
  statuses: LoggedInUserProjectsUiQueryVariables['projectFilter']['status'],
  skip = false,
  pageSize = 50
) => {
  const [projects, setProjects] = useState<Projects>([])
  const [pageOffset, setPageOffset] = useState(0)
  const [fetchedAllProjects, setFetchedAllProjects] = useState(false)

  const { refetch } = useQuery<
    LoggedInUserProjectsUiQuery,
    LoggedInUserProjectsUiQueryVariables
  >(GET_LOGGED_IN_USER_PROJECTS, {
    fetchPolicy: 'cache-and-network',
    skip: skip || fetchedAllProjects,
    variables: {
      projectPaginationFilter: { limit: pageSize, offset: pageOffset },
      projectFilter: { status: statuses },
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!data?.projectsForBuilder?.length) {
        setFetchedAllProjects(true)
        return
      }

      const total = data.projectsForBuilderAggregation.totalCount

      setProjects((prev) => {
        const newAllProjects = uniqBy(
          [...prev, ...data.projectsForBuilder],
          (p) => p.id
        )

        if (total > newAllProjects.length) {
          setPageOffset((prev) => prev + pageSize) // fetch next page of projects
        } else {
          setFetchedAllProjects(true) // we've fetched all projects
        }

        return newAllProjects
      })
    },
  })

  const refetchProjects = useCallback(() => {
    setPageOffset(0)
    setProjects([])
    setFetchedAllProjects(false)
    refetch()
  }, [refetch])

  return { projects, fetchedAllProjects, refetchProjects }
}
